import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PractitionersApi, PractitionersSearchRequest } from '~/api';
import { getBaseUrl } from '~/env';
import type { LocationState } from './location';
import type { DisciplineState } from './discipline';
import { MapDataState } from './mapData';
import { SearchFiltersState } from './searchFilters';
import { getDateRangeForAvailability } from '~/utilities/helpers';
import { groupSearchResultsByLocation } from '~/utilities/groupSearchResultsByLocation';
import { AvailabilityFilters, SearchResultGroupByLocation } from '~/utilities';

export interface SearchState {
  items?: SearchResultGroupByLocation[];
  error: string | null;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  hasSearched?: boolean;
  nextToken?: string | null;
  hasMore?: boolean;
}

type RootState = {
  location: LocationState;
  discipline: DisciplineState;
  mapData: MapDataState;
  search: SearchState;
  searchFilters: SearchFiltersState;
};

export const initialState: SearchState = {
  error: null,
  loading: 'idle',
  hasSearched: false,
};

export const searchPractitionersByLocation = createAsyncThunk(
  'practitioners/search',
  async (params: { loadMore?: boolean } | undefined, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const location = state.location.value;
    const discipline = state.discipline.value;
    const mapBounds = state.mapData.mapBounds;
    const availabilityFilter = state.searchFilters.availability;

    if (!discipline || !mapBounds || !location?.coordinates) {
      throw new Error(
        `Search was triggered with undefined values. location: ${location} discipline: ${discipline} mapBounds: ${mapBounds}`
      );
    }

    const api = new PractitionersApi({ baseUrl: getBaseUrl() });
    const findAllRequest: PractitionersSearchRequest = {
      maxResults: 50,
      boundingBox: {
        top: mapBounds.north,
        right: mapBounds.east,
        bottom: mapBounds.south,
        left: mapBounds.west,
      },
      discipline: discipline,
      latitude: location.coordinates.lat,
      longitude: location.coordinates.lng,
      availabilityFilter: availabilityFilter,
      nextToken: params?.loadMore
        ? state.search.nextToken || undefined
        : undefined,
    };

    if (availabilityFilter !== AvailabilityFilters.All) {
      const dateRange = getDateRangeForAvailability(availabilityFilter);
      findAllRequest.dateRange =
        dateRange?.startDateUtc && dateRange?.endDateUtc
          ? {
              startDateUtc: dateRange.startDateUtc,
              endDateUtc: dateRange.endDateUtc,
            }
          : undefined;
    }

    const response = await api.search(findAllRequest);
    const data = await response.json();
    return {
      results: data.results,
      nextToken: data.nextToken,
      isLoadMore: params?.loadMore || false,
    };
  }
);

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    clearSearchResult: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(searchPractitionersByLocation.pending, (state, action) => {
      state.loading = 'pending';
      // Only clear items for fresh searches, not for load more
      if (!action.meta.arg?.loadMore) {
        state.items = undefined;
      }
      state.error = null;
      state.hasSearched = true;
    });
    builder.addCase(
      searchPractitionersByLocation.fulfilled,
      (state, action) => {
        state.loading = 'succeeded';

        state.items = groupSearchResultsByLocation(
          action.payload.results,
          action.payload.isLoadMore,
          state.items
        );

        state.nextToken = action.payload.nextToken;
        state.hasMore = action.payload.nextToken !== null;
        state.hasSearched = true;
      }
    );
    builder.addCase(searchPractitionersByLocation.rejected, (state) => {
      state.loading = 'failed';
      state.items = undefined;
      state.error = 'Failed to search for practitioners';
      state.hasSearched = true;
    });
  },
});

type SliceState = { search: SearchState };
export const selectSearchResults = (state: SliceState) => {
  return state.search.items;
};

export const { clearSearchResult } = searchSlice.actions;
export const selectSearchLoading = (state: SliceState) => state.search.loading;
export const selectHasSearched = (state: SliceState) =>
  state.search.hasSearched;
export const selectSearchHasMore = (state: SliceState) => state.search.hasMore;
export const selectSearchNextToken = (state: SliceState) =>
  state.search.nextToken;

export default searchSlice.reducer;
